import React, { useState } from 'react';
import { Spin, Drawer } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, PageLayout } from '../../ui';
import {
  useGetClientsControlSummaryQuery,
  useGetClientRoutesWithIssuesQuery,
  type ClientControlSummary,
  type ClientRouteWithIssues
} from '../../store/api/financeApi';

const ClientsControlPage: React.FC = () => {
  const { data, isLoading } = useGetClientsControlSummaryQuery({});
  const [drawerClientId, setDrawerClientId] = useState<number | null>(null);
  const [drawerClientName, setDrawerClientName] = useState('');

  const { data: routesData, isLoading: routesLoading } = useGetClientRoutesWithIssuesQuery(
    { clientId: drawerClientId! },
    { skip: drawerClientId === null }
  );

  React.useEffect(() => {
    console.log('PHASE III.1 CLIENT CONTROL READY');
  }, []);

  const handleRowClick = (record: ClientControlSummary) => {
    if (record.clientId === null) return;
    setDrawerClientId(record.clientId);
    setDrawerClientName(record.clientName);
  };

  const columns: ColumnsType<ClientControlSummary> = [
    { title: 'Клиент', dataIndex: 'clientName', key: 'clientName' },
    { title: 'Маршрутов', dataIndex: 'totalRoutes', key: 'totalRoutes', width: 100 },
    { title: 'С долгом', dataIndex: 'routesWithDebt', key: 'routesWithDebt', width: 100 },
    { title: 'Просрочено', dataIndex: 'routesWithOverdue', key: 'routesWithOverdue', width: 100 },
    {
      title: 'Общий долг',
      dataIndex: 'totalRemaining',
      key: 'totalRemaining',
      width: 110,
      render: (v: number) => (v != null ? v.toLocaleString() : '—')
    },
    {
      title: 'Просроченная сумма',
      dataIndex: 'overdueAmount',
      key: 'overdueAmount',
      width: 140,
      render: (v: number) => (v != null ? v.toLocaleString() : '—')
    },
    {
      title: 'Макс. просрочка (дн.)',
      dataIndex: 'maxOverdueDays',
      key: 'maxOverdueDays',
      width: 140
    },
    {
      title: 'Проблемы документов',
      dataIndex: 'routesWithDocsIssues',
      key: 'routesWithDocsIssues',
      width: 150
    }
  ];

  const drawerColumns: ColumnsType<ClientRouteWithIssues> = [
    { title: 'Маршрут', dataIndex: 'routeId', key: 'routeId', render: (v: number) => `#${v}` },
    { title: 'Тип', dataIndex: 'transportType', key: 'transportType', width: 100 },
    { title: 'Статус', dataIndex: 'status', key: 'status', width: 100 },
    {
      title: 'Остаток',
      key: 'remaining',
      width: 100,
      render: (_: any, r: ClientRouteWithIssues) =>
        r.receivable?.remaining != null ? r.receivable.remaining.toLocaleString() : '—'
    },
    {
      title: 'Просрочка (дн.)',
      key: 'maxDaysOverdue',
      width: 120,
      render: (_: any, r: ClientRouteWithIssues) => r.receivable?.maxDaysOverdue ?? '—'
    },
    {
      title: 'Документальные проблемы',
      dataIndex: 'docsIssuesDescription',
      key: 'docsIssuesDescription',
      ellipsis: true
    }
  ];

  if (isLoading) {
    return (
      <PageLayout title="Контроль клиентов" subtitle="Загрузка сводки…">
        <div className="text-center py-12">
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Контроль клиентов" subtitle="Дебиторка, просрочки и документы по клиентам">
      <DataTable
        rowKey={(r) => String(r.clientId ?? 'unassigned')}
        columns={columns}
        dataSource={data?.clients ?? []}
        pagination={false}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: {
            cursor: record.clientId !== null ? 'pointer' : 'default'
          }
        })}
      />

      <Drawer
        title={`Маршруты: ${drawerClientName}`}
        placement="right"
        width={720}
        onClose={() => setDrawerClientId(null)}
        open={drawerClientId !== null}
      >
        {routesLoading ? (
          <Spin />
        ) : !routesData?.routes?.length ? (
          <div>У клиента нет маршрутов с долгом, просрочкой или проблемами документов</div>
        ) : (
          <DataTable
            rowKey="routeId"
            columns={drawerColumns}
            dataSource={routesData.routes}
            pagination={false}
            size="small"
          />
        )}
      </Drawer>
    </PageLayout>
  );
};

export default ClientsControlPage;
